import React, { useEffect, useMemo, useState, useRef, useLayoutEffect, useCallback } from "react";
import {
  Typography,
  Paper,
  Box,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  Snackbar,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation, useNavigate } from "react-router-dom";

import TableToolTip from "../../components/ToolTips/TableToolTip";
import * as styles from "../Broker/Styles.js";
import { PageRoot } from "../Broker/Styles.js";
import IconComponent from "../../components/Icons/IconComponent";
import brokersService from "../../Endpoints/BrokersIBs.js";
import ArchiveService from "../../Endpoints/Archive.js";
import Table from "../Broker/Table.js";
import Cookies from "universal-cookie";
import NotificationModal from "../Generals/Notifications/NotificationModal.js";
import {
  BROKERS_IBS_PANEL_HEIGHT,
  BROKERS_IBS_REPORT_SEARCH_ID_STORAGE_KEY,
  BROKERS_WALLET_EMAIL_STORAGE_KEY,
} from "../Table/constants.js";

import {
  CreateBrokerIbModal,
  EditBrokerIbModal,
  ClearSuccessModal,
  ErrorModal,
  DepositWithdrawModal,
  CreateUserSuccessModal,
  UpdateUserSuccessModal,
} from "./Modal";
import { Phone } from "@mui/icons-material";

function formatPanelLabel(key) {
  const normalized = String(key ?? "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();

  if (!normalized) return "-";

  return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}:`;
}

function formatPanelValue(value) {
  if (value === undefined || value === null) return "-";
  if (typeof value === "string") return value.trim() || "-";
  if (Array.isArray(value)) return value.length ? JSON.stringify(value) : "-";
  if (typeof value === "object") {
    const entries = Object.entries(value).filter(([, nestedValue]) => {
      if (nestedValue === undefined || nestedValue === null) return false;
      if (typeof nestedValue === "string") return nestedValue.trim() !== "";
      if (Array.isArray(nestedValue)) return nestedValue.length > 0;
      return true;
    });

    return entries.length ? JSON.stringify(value) : "-";
  }

  return String(value);
}

function formatWalletAmount(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return num.toFixed(2);
}

function parseWalletBalanceValue(value) {
  const parsed = Number(value);
  if (Number.isFinite(parsed)) return parsed;
  return Number.NEGATIVE_INFINITY;
}

// Fallback only: the authoritative role comes from the BrokersIBs endpoint
// response (response.data.data.role), see extractAdminRoleFromResponse().
function readAdminRole() {
  try {
    const cookies = new Cookies();
    const direct = [
      cookies.get("role"),
      cookies.get("user_role"),
      cookies.get("userRole"),
      cookies.get("admin_role"),
    ].find((value) => value && typeof value !== "object");

    if (direct) return String(direct).trim().toUpperCase();

    const blobs = [
      cookies.get("userData"),
      cookies.get("session"),
      cookies.get("admin"),
      cookies.get("profile"),
    ];

    for (const blob of blobs) {
      if (!blob) continue;
      const parsed = typeof blob === "string" ? JSON.parse(blob) : blob;
      const role = parsed?.role || parsed?.data?.role;
      if (role) return String(role).trim().toUpperCase();
    }
  } catch (error) {
    return "";
  }

  return "";
}

// Identity of the logged-in user (the "user" cookie holds the session email).
// It is used to make sure the cached view memory (data + admin role) is never
// reused by a different user that logs in later in the same browser tab.
function readSessionUser() {
  try {
    const cookies = new Cookies();
    const raw = cookies.get("user");
    const value =
      raw && typeof raw === "object" ? raw?.email ?? raw?.data?.email : raw;
    return String(value ?? "").trim().toLowerCase();
  } catch (error) {
    return "";
  }
}

function normalizeAdminRole(value) {
  return String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
}

function isSuperAdminRole(value) {
  return normalizeAdminRole(value).replace(/_/g, "") === "SUPERADMIN";
}

// The BrokersIBs endpoint answers:
// { status: "Success", data: { email, ip, role: "SUPER_ADMIN", users: [...] } }
function extractAdminRoleFromResponse(response) {
  const candidates = [
    response?.data?.data?.role,
    response?.data?.role,
    response?.data?.data?.user?.role,
    response?.data?.user?.role,
  ];
  const found = candidates.find(
    (value) => typeof value === "string" && value.trim() !== ""
  );

  return found ? normalizeAdminRole(found) : "";
}

function isWalletBalanceZero(row) {
  const raw = row?.walletBalanceValue ?? row?.wallet?.balance ?? row?.walletBalance;
  const numeric = Number(String(raw ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(numeric) && numeric === 0;
}

function getDeleteServiceMessage(source, fallback) {
  return (
    source?.response?.data?.data?.message ||
    source?.response?.data?.message ||
    source?.data?.data?.message ||
    source?.data?.message ||
    source?.message ||
    fallback
  );
}

// Any 2xx answer is a success; anything else (or an "Error" payload) is a failure.
function isFailedServiceResponse(response) {
  if (!response) return true;
  const status = Number(response.status);
  if (Number.isFinite(status) && (status < 200 || status >= 300)) return true;
  return (
    String(response?.data?.status ?? "").trim().toLowerCase() === "error" ||
    response?.data?.success === false
  );
}

function toCount(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

// The archived-accounts endpoint answers with the same kind of user objects as
// the BrokersIBs endpoint, plus wallet_transactions and pending_request.
function extractArchivedList(response) {
  const payload = response?.data?.data ?? response?.data ?? null;
  if (Array.isArray(payload)) return payload;

  const listKeys = ["users", "archived", "archived_users", "accounts", "clients"];
  for (const key of listKeys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }

  return [];
}

const ARCHIVE_CONFIRM_MESSAGE =
  "You are attempting to archive this account. Please note that there must be no relationship with any Tree before initiating the operation.";
const RESTORE_CONFIRM_MESSAGE =
  "The selected account will be reset. Please note that the user will be able to access it immediately, provided their account allows it.";
const DELETE_ARCHIVED_CONFIRM_MESSAGE =
  "The archived account will be deleted. This operation cannot be reversed once it has been successfully completed.";

function maskPassword(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "-";
  return "*".repeat(raw.length);
}

const EMPTY_IB_PANEL_ITEM = {
  id: "-",
  name: "-",
  email: "-",
  password: "-",
  wallet: "-",
};

const BROKERS_IBS_TABS = ["BROKERS", "IBS", "ARCHIVED"];
const IB_REFERRAL_LINK_BASE =
  "https://cabinet.tradeviewmarkets.com/en/register_ib_portal/1/live-account/?ib=";

function normalizeBrokersIbsTab(value) {
  const normalized = String(value || "").trim().toUpperCase();

  if (normalized === "BROKER") return "BROKERS";
  if (normalized === "IB") return "IBS";
  if (normalized === "ARCHIVE") return "ARCHIVED";

  return BROKERS_IBS_TABS.includes(normalized) ? normalized : "BROKERS";
}

function getBrokersIbsTabFromLocation(location) {
  const tabFromSearch = new URLSearchParams(location.search).get("tab");
  return normalizeBrokersIbsTab(tabFromSearch || location.state?.brokersIbsTab);
}

function normalizeSelectionIds(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (value instanceof Set) return Array.from(value);
  if (Array.isArray(value.ids)) return value.ids;
  if (value.ids instanceof Set) return Array.from(value.ids);

  return [];
}

function getBrokersIbsSelectionFromLocation(location) {
  const selection = location.state?.brokersIbsSelection;

  return {
    BROKERS: normalizeSelectionIds(selection?.BROKERS ?? selection?.brokers),
    IBS: normalizeSelectionIds(selection?.IBS ?? selection?.ibs),
  };
}

function getBrokersIbsSearchFromLocation(location) {
  const searchValue = location.state?.brokersIbsSearch;
  return typeof searchValue === "string" ? searchValue : "";
}

function buildBrokersIbsLocationState(
  locationState,
  tab,
  selectionOverrides = {},
  searchOverride
) {
  const currentSelection =
    locationState?.brokersIbsSelection &&
    typeof locationState.brokersIbsSelection === "object"
      ? locationState.brokersIbsSelection
      : {};
  const currentSearch =
    typeof locationState?.brokersIbsSearch === "string"
      ? locationState.brokersIbsSearch
      : "";

  return {
    ...(locationState && typeof locationState === "object" ? locationState : {}),
    brokersIbsTab: tab,
    brokersIbsSearch: searchOverride ?? currentSearch,
    brokersIbsSelection: {
      BROKERS: normalizeSelectionIds(
        selectionOverrides.BROKERS ??
          currentSelection.BROKERS ??
          currentSelection.brokers
      ),
      IBS: normalizeSelectionIds(
        selectionOverrides.IBS ?? currentSelection.IBS ?? currentSelection.ibs
      ),
    },
  };
}

function getBrokerIbsSortId(row) {
  return row?.userId ?? row?.id ?? "";
}

function compareBrokerIbsRowsByIdDesc(a, b) {
  const rawA = getBrokerIbsSortId(a);
  const rawB = getBrokerIbsSortId(b);
  const numA = Number(rawA);
  const numB = Number(rawB);
  const hasNumericA = String(rawA).trim() !== "" && Number.isFinite(numA);
  const hasNumericB = String(rawB).trim() !== "" && Number.isFinite(numB);

  if (hasNumericA && hasNumericB) return numB - numA;
  if (hasNumericA) return -1;
  if (hasNumericB) return 1;

  return String(rawB).localeCompare(String(rawA), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

function getBrokersIbsMemory() {
  const root = window.__crmViewMemory || (window.__crmViewMemory = {});
  const sessionUser = readSessionUser();

  // The memory belongs to ONE logged-in user. If another user logged in (for
  // example SUPER_ADMIN logged out and an ADMIN logged in, without a full page
  // reload) everything cached for the previous user is dropped, including the
  // admin role. Otherwise the previous role would keep showing "Delete Account".
  if (root.brokersIbs && root.brokersIbs.sessionUser !== sessionUser) {
    root.brokersIbs = null;
  }

  if (!root.brokersIbs) {
    root.brokersIbs = {
      sessionUser,
      hydrated: false,
      forceRefresh: false,
      data: [],
      adminRole: "",
      adminRoleLoaded: false,
      archivedData: [],
      archivedLoaded: false,
      search: "",
      activeTab: "BROKERS",
      selectedBrokerIds: [],
      selectedIbIds: [],
      sortField: "userId",
      sortDirection: "asc",
      scrollPosition: null,
      showSidePanel: false,
      panelItems: [],
      panelCache: {},
      activePanelRowId: null,
      actionHighlightRowId: null,
      isPanelExpanded: false,
    };
  }

  return root.brokersIbs;
}

function findScrollParent(anchor) {
  let current = anchor?.parentElement || null;

  while (current && current !== document.body && current !== document.documentElement) {
    const style = window.getComputedStyle(current);
    const overflowY = `${style.overflowY} ${style.overflow}`;
    const canScroll =
      /(auto|scroll)/.test(overflowY) && current.scrollHeight > current.clientHeight + 1;

    if (canScroll) return current;
    current = current.parentElement;
  }

  return null;
}

function readScrollPosition(anchor) {
  const scroller = findScrollParent(anchor);

  if (!scroller) {
    return {
      top: window.scrollY || document.documentElement.scrollTop || 0,
      left: window.scrollX || document.documentElement.scrollLeft || 0,
      mode: "window",
    };
  }

  return {
    top: scroller.scrollTop,
    left: scroller.scrollLeft,
    mode: "element",
  };
}

function restoreScrollPosition(anchor, position) {
  if (!position) return;

  const apply = () => {
    const scroller = findScrollParent(anchor);

    if (position.mode === "element" && scroller) {
      scroller.scrollTop = position.top || 0;
      scroller.scrollLeft = position.left || 0;
      return;
    }

    window.scrollTo(position.left || 0, position.top || 0);
  };

  apply();
  window.requestAnimationFrame(apply);
}

function InfoCard({ item }) {
  const labelSx = { fontWeight: 800, fontSize: 12, color: "#111827" };
  const valueSx = { fontSize: 12, color: "#111827", lineHeight: 1.15 };
  const visibleEntries = Object.entries(item ?? {});

  return (
    <Box sx={{ background: "#EDF4FB", borderRadius: 4, p: 2.2 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
        {visibleEntries.length > 0 ? (
          visibleEntries.map(([key, value]) => (
            <Box key={key}>
              <Typography sx={labelSx}>{formatPanelLabel(key)}</Typography>
              <Typography sx={{ ...valueSx, wordBreak: "break-word" }}>
                {formatPanelValue(value)}
              </Typography>
            </Box>
          ))
        ) : (
          <Box sx={{ gridColumn: "1 / -1" }}>
            <Typography sx={labelSx}>Info:</Typography>
            <Typography sx={valueSx}>-</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
function BrokerIbs() {
  const navigate = useNavigate();
  const location = useLocation();
  const pageRef = useRef(null);
  const memory = getBrokersIbsMemory();
  const locationTab = getBrokersIbsTabFromLocation(location);
  const locationSelection = getBrokersIbsSelectionFromLocation(location);
  const locationSearch = getBrokersIbsSearchFromLocation(location);
  const skipTabSync = useRef(Boolean(memory.hydrated));
  const skipSelectionSync = useRef(Boolean(memory.hydrated));
  const skipSearchSync = useRef(Boolean(memory.hydrated));

  const [data, setData] = useState(() =>
    memory.hydrated && Array.isArray(memory.data) ? memory.data : []
  );
  const [search, setSearch] = useState(
    memory.hydrated ? memory.search ?? "" : locationSearch
  );

  const [loading, setLoading] = useState(() => !memory.hydrated);
  const [dwLoading, setDwLoading] = useState(false);

  const [selectedBrokerIds, setSelectedBrokerIds] = useState(
    memory.hydrated ? memory.selectedBrokerIds || [] : locationSelection.BROKERS
  );
  const [selectedIbIds, setSelectedIbIds] = useState(
    memory.hydrated ? memory.selectedIbIds || [] : locationSelection.IBS
  );

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openSuccessClear, setOpenSuccessClear] = useState(false);

  const [openCreateSuccess, setOpenCreateSuccess] = useState(false);
  const [createSuccessMessage, setCreateSuccessMessage] = useState("");

  const [openUpdateSuccess, setOpenUpdateSuccess] = useState(false);
  const [updatedUserName, setUpdatedUserName] = useState("");

  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Admin role: it arrives in the BrokersIBs response (data.role), so it has to
  // be state (it is not known on the first render) and it is also kept in the
  // view memory so it survives the "Go back" restore, which skips the fetch.
  const [adminRole, setAdminRole] = useState(
    () => memory.adminRole || readAdminRole()
  );
  const isSuperAdmin = isSuperAdminRole(adminRole);
  // True once the BrokersIBs answer (the one that carries the role) was received.
  // Until then the role is unknown, so the Archived tab is neither shown nor removed.
  const [adminRoleResolved, setAdminRoleResolved] = useState(() =>
    Boolean(memory.adminRoleLoaded)
  );
  const [rowToDelete, setRowToDelete] = useState(null);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteNotice, setDeleteNotice] = useState({
    open: false,
    iconType: "error",
    title: "",
    message: "",
  });
  // Archived accounts ("Archived" tab) + Archive / Restore / Delete flows
  const [archivedData, setArchivedData] = useState(() =>
    Array.isArray(memory.archivedData) ? memory.archivedData : []
  );
  const [archivedLoaded, setArchivedLoaded] = useState(() =>
    Boolean(memory.archivedLoaded)
  );
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [archivedError, setArchivedError] = useState("");
  const accountActionBusyRef = useRef(false);
  const [rowToArchive, setRowToArchive] = useState(null);
  const [openArchiveConfirm, setOpenArchiveConfirm] = useState(false);
  const [archivedActionConfirm, setArchivedActionConfirm] = useState({
    open: false,
    type: "",
    row: null,
  });
  const [accountActionNotice, setAccountActionNotice] = useState({
    open: false,
    iconType: "error",
    title: "",
    message: "",
  });
  const [copyLinkFeedback, setCopyLinkFeedback] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const [activeTab, setActiveTab] = useState(
    memory.hydrated ? memory.activeTab || locationTab : locationTab
  );

  // The Archived tab and all of its actions are for SUPER_ADMIN only.
  const isArchivedView = activeTab === "ARCHIVED" && isSuperAdmin;
  const showArchivedTab =
    isSuperAdmin || (activeTab === "ARCHIVED" && !adminRoleResolved);

  const selectedUsersIds = useMemo(() => {
    if (activeTab === "BROKERS") return selectedBrokerIds;
    if (activeTab === "IBS") return selectedIbIds;
    return [];
  }, [activeTab, selectedBrokerIds, selectedIbIds]);

  const [showSidePanel, setShowSidePanel] = useState(Boolean(memory.showSidePanel));
  const [panelLoading, setPanelLoading] = useState(false);
  const [panelError, setPanelError] = useState("");
  const [panelItems, setPanelItems] = useState(memory.panelItems || []);
  const [panelCache, setPanelCache] = useState(memory.panelCache || {});
  const [activePanelRowId, setActivePanelRowId] = useState(memory.activePanelRowId ?? null);
  const [actionHighlightRowId, setActionHighlightRowId] = useState(
    memory.actionHighlightRowId ?? null
  );
  const [isPanelExpanded, setIsPanelExpanded] = useState(Boolean(memory.isPanelExpanded));

  const [openDW, setOpenDW] = useState(false);
  const [dwUser, setDwUser] = useState(null);
  const [dwEmail, setDwEmail] = useState("");

  const [openEditModal, setOpenEditModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [sortField, setSortField] = useState(memory.sortField || "userId");
  const [sortDirection, setSortDirection] = useState(memory.sortDirection || "asc");

  const rememberBrokersIbsView = useCallback(() => {
    const current = getBrokersIbsMemory();
    current.search = search;
    current.activeTab = activeTab;
    current.selectedBrokerIds = selectedBrokerIds;
    current.selectedIbIds = selectedIbIds;
    current.sortField = sortField;
    current.sortDirection = sortDirection;
    current.data = data;
    current.showSidePanel = showSidePanel;
    current.panelItems = panelItems;
    current.panelCache = panelCache;
    current.activePanelRowId = activePanelRowId;
    current.actionHighlightRowId = actionHighlightRowId;
    current.isPanelExpanded = isPanelExpanded;
    current.scrollPosition = readScrollPosition(pageRef.current);
  }, [
    search,
    activeTab,
    selectedBrokerIds,
    selectedIbIds,
    sortField,
    sortDirection,
    data,
    showSidePanel,
    panelItems,
    panelCache,
    activePanelRowId,
    actionHighlightRowId,
    isPanelExpanded,
  ]);

  const markLeaveForReturn = () => {
    rememberBrokersIbsView();
    getBrokersIbsMemory().forceRefresh = false;
    try {
      sessionStorage.setItem("brokersIbs.skipNextFetch", "1");
    } catch (error) {
      // ignore
    }
  };

  useLayoutEffect(() => {
    if (!memory.hydrated || !memory.scrollPosition) return undefined;
    restoreScrollPosition(pageRef.current, memory.scrollPosition);
    const timer = window.setTimeout(() => {
      restoreScrollPosition(pageRef.current, memory.scrollPosition);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [memory]);

  useEffect(() => {
    const scroller = findScrollParent(pageRef.current) || window;
    const saveScroll = () => {
      getBrokersIbsMemory().scrollPosition = readScrollPosition(pageRef.current);
    };
    scroller.addEventListener("scroll", saveScroll, { passive: true });
    return () => {
      saveScroll();
      rememberBrokersIbsView();
      scroller.removeEventListener("scroll", saveScroll);
    };
  }, [rememberBrokersIbsView]);

  useEffect(() => {
    const current = getBrokersIbsMemory();
    current.search = search;
    current.activeTab = activeTab;
    current.selectedBrokerIds = selectedBrokerIds;
    current.selectedIbIds = selectedIbIds;
    current.sortField = sortField;
    current.sortDirection = sortDirection;
    current.data = data;
    current.showSidePanel = showSidePanel;
    current.panelItems = panelItems;
    current.panelCache = panelCache;
    current.activePanelRowId = activePanelRowId;
    current.actionHighlightRowId = actionHighlightRowId;
    current.isPanelExpanded = isPanelExpanded;
  }, [
    search,
    activeTab,
    selectedBrokerIds,
    selectedIbIds,
    sortField,
    sortDirection,
    data,
    showSidePanel,
    panelItems,
    panelCache,
    activePanelRowId,
    actionHighlightRowId,
    isPanelExpanded,
  ]);

  useEffect(() => {
    if (skipTabSync.current) {
      skipTabSync.current = false;
      return;
    }

    setActiveTab((prev) => (prev === locationTab ? prev : locationTab));

    if (locationTab !== "BROKERS") {
      setShowSidePanel(false);
      setPanelItems([]);
      setPanelError("");
      setPanelLoading(false);
      setActivePanelRowId(null);
      setIsPanelExpanded(false);
    }
  }, [locationTab]);

  useEffect(() => {
    if (skipSelectionSync.current) {
      skipSelectionSync.current = false;
      return;
    }

    const nextSelection = getBrokersIbsSelectionFromLocation(location);
    setSelectedBrokerIds(nextSelection.BROKERS);
    setSelectedIbIds(nextSelection.IBS);
  }, [location]);

  useEffect(() => {
    if (skipSearchSync.current) {
      skipSearchSync.current = false;
      return;
    }

    setSearch((prev) => (prev === locationSearch ? prev : locationSearch));
  }, [locationSearch]);

  const handleOpenDeleteAccount = (row) => {
    if (!isSuperAdmin) return;

    if (!isWalletBalanceZero(row)) {
      setDeleteNotice({
        open: true,
        iconType: "error",
        title: "Account not deleted",
        message: "The account can only be deleted when the wallet balance is 0.",
      });
      return;
    }

    setRowToDelete(row);
    setOpenDeleteConfirm(true);
  };

  const handleCloseDeleteConfirm = () => {
    if (deletingAccount) return;
    setOpenDeleteConfirm(false);
  };

  const handleConfirmDeleteAccount = async () => {
    const account = rowToDelete;
    const accountId = account?.id ?? account?.userId;

    if (!accountId) {
      setOpenDeleteConfirm(false);
      setDeleteNotice({
        open: true,
        iconType: "error",
        title: "Account not deleted",
        message: "This account does not have an ID available.",
      });
      return;
    }

    setDeletingAccount(true);

    try {
      const response = await brokersService.DeleteBroker(accountId);
      const failed =
        !response ||
        (response.status && response.status !== 200 && response.status !== 204) ||
        response?.data?.status === "Error" ||
        response?.data?.success === false;

      if (failed) {
        setDeleteNotice({
          open: true,
          iconType: "error",
          title: "Account not deleted",
          message: getDeleteServiceMessage(
            response,
            "The account could not be deleted."
          ),
        });
        return;
      }

      setData((prev) => {
        const next = prev.filter((item) => item.id !== account.id && item.userId !== accountId);
        const memory = getBrokersIbsMemory();
        memory.data = next;
        return next;
      });
      setSelectedBrokerIds((prev) => prev.filter((id) => String(id) !== String(accountId)));
      setSelectedIbIds((prev) => prev.filter((id) => String(id) !== String(accountId)));
      setDeleteNotice({
        open: true,
        iconType: "success",
        title: "Account deleted",
        message: getDeleteServiceMessage(
          response,
          "The account was permanently deleted and cannot be recovered."
        ),
      });
      setRowToDelete(null);
    } catch (error) {
      setDeleteNotice({
        open: true,
        iconType: "error",
        title: "Account not deleted",
        message: getDeleteServiceMessage(error, "The account could not be deleted."),
      });
    } finally {
      setDeletingAccount(false);
      setOpenDeleteConfirm(false);
    }
  };

  const handleOpenError = (message) => {
    setErrorMessage(message);
    setOpenErrorModal(true);
  };

  const handleCloseError = () => {
    setOpenErrorModal(false);
    setErrorMessage("");
  };

  const mapApiUsersToRows = (list = []) => {
    return list.map((item, idx) => {
      const walletCurrency = item?.wallet?.currency ?? "";
      const walletBalanceNum = Number(item?.wallet?.balance);
      const walletBalanceFormatted = formatWalletAmount(walletBalanceNum);

      const walletBalanceLabel =
        walletBalanceFormatted !== null && walletCurrency
          ? `${walletBalanceFormatted} ${walletCurrency}`
          : item?.walletBalance ?? item?.balance ?? "0 USD";

      let registration = item.registration_date ?? "";

      if (typeof registration === "string" && registration) {
        registration = registration.split(/[T ]/)[0];
      }

      return {
        id: item.id ?? idx + 1,
        userId: item.id ?? "",
        name: item.name ?? "",
        email: item.email ?? "",

        reportEmail: item.reportEmail ?? item.report_email ?? "",
        status: item.account_status ?? item.status ?? "",
        brokerCode: item.brokerCode ?? item.broker_code ?? item.status ?? "",
        password: item?.password ?? "",
        userType: item.userType ?? item.user_type ?? item.type ?? "",
        trees: item.trees ?? [],
        currency: walletCurrency,
        walletBalance: walletBalanceLabel,
        walletBalanceValue: walletBalanceNum,
        wallet: item.wallet ?? null,
        country: item.country ?? "",
        city: item.city ?? "",
        address: item.address ?? "",
        phone: item.phone ?? "",
        registration_date: registration,
        ibs: Array.isArray(item?.ibs) ? item.ibs : [],
      };
    });
  };

  const reloadUsers = async () => {
    const response = await brokersService.BrokersIBs();
    if (!response) {
      if (!getBrokersIbsMemory().hydrated) setData([]);
      return false;
    }

    // The logged-in admin role comes in the same response: data.role.
    // The latest response is authoritative: it always overrides whatever role
    // we had before (e.g. "ADMIN" must replace a previous "SUPER_ADMIN").
    // The cookie is only a fallback when the response has no role.
    const responseRole = extractAdminRoleFromResponse(response);
    const nextRole = responseRole || readAdminRole();
    const brokersIbsMemory = getBrokersIbsMemory();
    brokersIbsMemory.adminRole = nextRole;
    // The role can only be reused later (the "Go back" restore skips the
    // fetch) when we know which user it belongs to.
    brokersIbsMemory.adminRoleLoaded = Boolean(brokersIbsMemory.sessionUser);
    setAdminRole(nextRole);
    setAdminRoleResolved(true);

    const list = Array.isArray(response?.data?.data?.users)
      ? response.data.data.users
      : [];
    const mapped = mapApiUsersToRows(list);
    getBrokersIbsMemory().data = mapped;
    getBrokersIbsMemory().hydrated = true;
    setData(mapped);
    return true;
  };

  useEffect(() => {
    const current = getBrokersIbsMemory();
    // Skip the request only when the table AND the admin role were already
    // loaded; otherwise the role would never reach the component.
    if (current.hydrated && !current.forceRefresh && current.adminRoleLoaded) {
      current.forceRefresh = false;
      setLoading(false);
      return undefined;
    }

    current.forceRefresh = false;
    let alive = true;

    const fetchApi = async () => {
      if (!current.hydrated) setLoading(true);
      try {
        const loaded = await reloadUsers();
        if (!alive) return;
        if (!loaded && !current.hydrated) {
          handleOpenError("Error fetching brokers data. Please try again.");
        }
      } catch (err) {
        if (alive && !current.hydrated) {
          handleOpenError("Error fetching brokers data. Please try again.");
        }
      } finally {
        if (alive) {
          setLoading(false);
          setAdminRoleResolved(true);
        }
      }
    };

    fetchApi();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalizeType = (v) => String(v || "").trim().toUpperCase();

  const isIB = (row) => {
    const t = normalizeType(row?.userType);
    return (
      t === "IB" ||
      t === "INTRODUCING_BROKER" ||
      t === "INTRODUCER" ||
      t === "INTRODUCINGBROKER"
    );
  };

  const isBroker = (row) => {
    const t = normalizeType(row?.userType);
    if (!t) return true;
    return t === "BROKER";
  };

  const brokersRowsBase = useMemo(
    () => data.filter((r) => isBroker(r) && !isIB(r)),
    [data]
  );

  const ibsRowsBase = useMemo(
    () => data.filter((r) => isIB(r) && !isBroker(r)),
    [data]
  );

  const rowsBase = useMemo(() => {
    if (isArchivedView) return archivedData;
    return activeTab === "IBS" ? ibsRowsBase : brokersRowsBase;
  }, [activeTab, isArchivedView, archivedData, brokersRowsBase, ibsRowsBase]);

  const filteredRows = useMemo(() => {
    const lower = search.toLowerCase();
    return rowsBase.filter(
      (u) =>
        (u.name || "").toLowerCase().includes(lower) ||
        (u.email || "").toLowerCase().includes(lower) ||
        String(u.userId || "").toLowerCase().includes(lower) ||
        String(u.brokerCode || "").toLowerCase().includes(lower) ||
        String(u.accountType || "").toLowerCase().includes(lower)
    );
  }, [rowsBase, search]);

  const sortedRows = useMemo(() => {
    const directionFactor = sortDirection === "asc" ? 1 : -1;
    const rowsToSort = [...filteredRows];

    rowsToSort.sort((a, b) => {
      let result = 0;

      if (sortField === "userId") {
        result = -compareBrokerIbsRowsByIdDesc(a, b);
      } else if (sortField === "walletBalance") {
        const aValue = parseWalletBalanceValue(a?.walletBalanceValue);
        const bValue = parseWalletBalanceValue(b?.walletBalanceValue);
        result = aValue - bValue;
      } else if (
        sortField === "walletTransactions" ||
        sortField === "pendingRequest"
      ) {
        result = (Number(a?.[sortField]) || 0) - (Number(b?.[sortField]) || 0);
      } else if (
        sortField === "name" ||
        sortField === "email" ||
        sortField === "status" ||
        sortField === "brokerCode" ||
        sortField === "accountType"
      ) {
        result = String(a?.[sortField] ?? "").localeCompare(
          String(b?.[sortField] ?? ""),
          undefined,
          { sensitivity: "base", numeric: true }
        );
      }

      if (result === 0) {
        return compareBrokerIbsRowsByIdDesc(a, b);
      }

      return result * directionFactor;
    });

    return rowsToSort;
  }, [filteredRows, sortDirection, sortField]);

  // Archived is SUPER_ADMIN only. Once the role is known, anyone else that lands on
  // it (saved view memory, a bookmarked ?tab=archived, ...) is sent back to Brokers.
  useEffect(() => {
    if (!adminRoleResolved || isSuperAdmin || activeTab !== "ARCHIVED") return;

    const params = new URLSearchParams(location.search);
    params.set("tab", "brokers");

    setActiveTab("BROKERS");
    navigate(
      { pathname: location.pathname, search: `?${params.toString()}` },
      {
        replace: true,
        state: buildBrokersIbsLocationState(
          location.state,
          "BROKERS",
          { BROKERS: selectedBrokerIds, IBS: selectedIbIds },
          search
        ),
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminRoleResolved, isSuperAdmin, activeTab]);

  /* ---------- ARCHIVED ACCOUNTS: list + Archive / Restore / Delete ---------- */
  const mapArchivedUsersToRows = (list = []) =>
    mapApiUsersToRows(list).map((row, index) => {
      const source = list[index] || {};

      return {
        ...row,
        accountType: isIB(row)
          ? "IB"
          : isBroker(row)
            ? "Broker"
            : String(row.userType || "-"),
        walletTransactions: toCount(
          source.wallet_transactions ?? source.walletTransactions
        ),
        pendingRequest: toCount(source.pending_request ?? source.pendingRequest),
      };
    });

  useEffect(() => {
    if (!isArchivedView || archivedLoaded) return undefined;

    let alive = true;

    const fetchArchived = async () => {
      setArchivedLoading(true);
      setArchivedError("");

      try {
        const response = await ArchiveService.Archive();
        if (!alive) return;

        if (!response) {
          setArchivedError(
            "We were unable to load the archived accounts. Please try again."
          );
          return;
        }

        const mapped = mapArchivedUsersToRows(extractArchivedList(response));
        const brokersIbsMemory = getBrokersIbsMemory();
        brokersIbsMemory.archivedData = mapped;
        brokersIbsMemory.archivedLoaded = true;
        setArchivedData(mapped);
        setArchivedLoaded(true);
      } catch (error) {
        if (alive) {
          setArchivedError(
            getDeleteServiceMessage(
              error,
              "We were unable to load the archived accounts. Please try again."
            )
          );
        }
      } finally {
        if (alive) setArchivedLoading(false);
      }
    };

    fetchArchived();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isArchivedView, archivedLoaded]);

  const showAccountActionNotice = (iconType, title, message) => {
    setAccountActionNotice({ open: true, iconType, title, message });
  };

  // ----- Archive User (Brokers / IBs tabs, SUPER_ADMIN only) -----
  const handleOpenArchiveAccount = (row) => {
    if (!isSuperAdmin) return;
    setRowToArchive(row);
    setOpenArchiveConfirm(true);
  };

  const handleCloseArchiveConfirm = () => {
    setOpenArchiveConfirm(false);
  };

  const handleConfirmArchiveAccount = async () => {
    if (!isSuperAdmin || accountActionBusyRef.current) return;

    const account = rowToArchive;
    const accountId = account?.id ?? account?.userId;

    if (!accountId) {
      showAccountActionNotice(
        "error",
        "Account not archived",
        "This account does not have an ID available."
      );
      return;
    }

    accountActionBusyRef.current = true;

    try {
      const response = await ArchiveService.ArchiveUser(accountId);

      if (isFailedServiceResponse(response)) {
        showAccountActionNotice(
          "error",
          "Account not archived",
          getDeleteServiceMessage(response, "The account could not be archived.")
        );
        return;
      }

      setData((prev) => {
        const next = prev.filter(
          (item) =>
            String(item.id) !== String(accountId) &&
            String(item.userId) !== String(accountId)
        );
        getBrokersIbsMemory().data = next;
        return next;
      });
      setSelectedBrokerIds((prev) => prev.filter((id) => String(id) !== String(accountId)));
      setSelectedIbIds((prev) => prev.filter((id) => String(id) !== String(accountId)));
      if (String(activePanelRowId) === String(accountId)) handleCloseSidePanel();

      // The archived list changed: it is loaded again the next time the tab opens.
      getBrokersIbsMemory().archivedLoaded = false;
      setArchivedLoaded(false);

      setRowToArchive(null);
      showAccountActionNotice(
        "success",
        "Account archived",
        getDeleteServiceMessage(response, "The account was archived successfully.")
      );
    } catch (error) {
      showAccountActionNotice(
        "error",
        "Account not archived",
        getDeleteServiceMessage(error, "The account could not be archived.")
      );
    } finally {
      accountActionBusyRef.current = false;
    }
  };

  // ----- Archived tab: Restore / Delete -----
  const handleOpenRestoreAccount = (row) => {
    if (!isSuperAdmin) return;
    setArchivedActionConfirm({ open: true, type: "restore", row });
  };

  const handleOpenDeleteArchivedAccount = (row) => {
    if (!isSuperAdmin) return;
    setArchivedActionConfirm({ open: true, type: "delete", row });
  };

  const handleCloseArchivedActionConfirm = () => {
    setArchivedActionConfirm((current) => ({ ...current, open: false }));
  };

  const handleConfirmArchivedAction = async () => {
    if (!isSuperAdmin || accountActionBusyRef.current) return;

    const { type, row: account } = archivedActionConfirm;
    const accountId = account?.id ?? account?.userId;
    const isRestore = type === "restore";
    const failedTitle = isRestore ? "Account not restored" : "Archived account not deleted";

    if (!accountId) {
      showAccountActionNotice(
        "error",
        failedTitle,
        "This account does not have an ID available."
      );
      return;
    }

    accountActionBusyRef.current = true;

    try {
      const response = isRestore
        ? await ArchiveService.RestoreUser(accountId)
        : await ArchiveService.DeleteArchivedUser(accountId);

      if (isFailedServiceResponse(response)) {
        showAccountActionNotice(
          "error",
          failedTitle,
          getDeleteServiceMessage(
            response,
            isRestore
              ? "The account could not be restored."
              : "The archived account could not be deleted."
          )
        );
        return;
      }

      setArchivedData((prev) => {
        const next = prev.filter(
          (item) =>
            String(item.id) !== String(accountId) &&
            String(item.userId) !== String(accountId)
        );
        getBrokersIbsMemory().archivedData = next;
        return next;
      });

      if (isRestore) {
        // The account is active again: refresh the Brokers / IBs lists.
        reloadUsers().catch(() => {});
      }

      showAccountActionNotice(
        "success",
        isRestore ? "Account restored" : "Archived account deleted",
        getDeleteServiceMessage(
          response,
          isRestore
            ? "The account was restored successfully."
            : "The archived account was permanently deleted."
        )
      );
    } catch (error) {
      showAccountActionNotice(
        "error",
        failedTitle,
        getDeleteServiceMessage(
          error,
          isRestore
            ? "The account could not be restored."
            : "The archived account could not be deleted."
        )
      );
    } finally {
      accountActionBusyRef.current = false;
    }
  };

  const handleSortChange = (field) => {
    if (!field) return;

    setSortField((prevField) => {
      if (prevField !== field) {
        setSortDirection("asc");
        return field;
      }

      setSortDirection((prevDirection) => (prevDirection === "asc" ? "desc" : "asc"));
      return prevField;
    });
  };

  const replaceBrokersIbsState = (tab, selectionOverrides, searchValue) => {
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
      },
      {
        replace: true,
        state: buildBrokersIbsLocationState(
          location.state,
          tab,
          selectionOverrides,
          searchValue
        ),
      }
    );
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    replaceBrokersIbsState(
      activeTab,
      {
        BROKERS: selectedBrokerIds,
        IBS: selectedIbIds,
      },
      value
    );
  };

  const handleRowSelectionChange = (newSelection) => {
    if (activeTab === "ARCHIVED") return;
    const selection = normalizeSelectionIds(newSelection);
    const nextBrokerIds = activeTab === "BROKERS" ? selection : selectedBrokerIds;
    const nextIbIds = activeTab === "IBS" ? selection : selectedIbIds;

    if (activeTab === "BROKERS") setSelectedBrokerIds(selection);
    else setSelectedIbIds(selection);

    replaceBrokersIbsState(
      activeTab,
      {
        BROKERS: nextBrokerIds,
        IBS: nextIbIds,
      },
      search
    );
  };

  const mapIbPanelItems = (list = []) =>
    list.map((x, idx) => {
      const walletAmount = formatWalletAmount(x?.wallet?.balance);

      return {
        id: x?.id ?? x?.userId ?? idx + 1,
        name: x?.name ?? "",
        email: x?.email ?? "",
        password: maskPassword(x?.password),
        wallet: walletAmount !== null && x?.wallet?.currency ? `${walletAmount} ${x.wallet.currency}` : "-",
      };
    });

  const fetchPanelData = async (brokerRow) => {
    const brokerId = brokerRow?.id ?? brokerRow?.userId;
    if (!brokerId) {
      setPanelError("Invalid broker id.");
      return;
    }

    if (panelCache[brokerId]) {
      setPanelError("");
      setPanelLoading(false);
      setPanelItems(panelCache[brokerId]);
      return;
    }

    setPanelError("");
    setPanelLoading(false);

    const formatted = mapIbPanelItems(Array.isArray(brokerRow?.ibs) ? brokerRow.ibs : []);
    setPanelItems(formatted);
    setPanelCache((prev) => ({ ...prev, [brokerId]: formatted }));
  };

  const handleOpenSidePanel = async (row) => {
    if (activeTab !== "BROKERS") return;

    setActivePanelRowId(row?.id ?? row?.userId ?? null);
    setShowSidePanel(true);
    setPanelError("");
    setPanelLoading(false);
    setPanelItems([]);

    await fetchPanelData(row);
  };

  const handleActionMenuOpen = (row) => {
    setActionHighlightRowId(row?.id ?? row?.userId ?? null);
  };

  const handleCloseSidePanel = () => {
    setShowSidePanel(false);
    setPanelItems([]);
    setPanelError("");
    setPanelLoading(false);
    setActivePanelRowId(null);
    setIsPanelExpanded(false);
  };

  const handleEnable = async () => {
    if (selectedUsersIds.length === 0) {
      handleOpenError("Please select at least one row.");
      return;
    }
    try {
      setLoading(true);
      const response = await brokersService.BrokersIBsUpdateStatus(selectedUsersIds, "ACTIVE");
      if (!response) {
        handleOpenError("We were unable to save your changes, please try again.");
        return;
      }
      setData((prev) =>
        prev.map((u) =>
          selectedUsersIds.includes(u.id) ? { ...u, status: "ACTIVE" } : u
        )
      );
    } catch (err) {
      handleOpenError("We were unable to save your changes, please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (selectedUsersIds.length === 0) {
      handleOpenError("Please select at least one row.");
      return;
    }
    try {
      setLoading(true);
      const response = await brokersService.BrokersIBsUpdateStatus(selectedUsersIds, "INACTIVE");
      if (!response) {
        handleOpenError("We were unable to save your changes, please try again.");
        return;
      }
      setData((prev) =>
        prev.map((u) =>
          selectedUsersIds.includes(u.id) ? { ...u, status: "INACTIVE" } : u
        )
      );
    } catch (err) {
      handleOpenError("We were unable to save your changes, please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDepositWithdrawClick = (row) => {
    const email = String(row?.email ?? "").trim();
    if (!email) {
      handleOpenError("This user has no email. Cannot create a transaction.");
      return;
    }
    setDwUser(row);
    setDwEmail(email);
    setOpenDW(true);
  };

  const handleCloseDW = () => {
    setOpenDW(false);
    setDwUser(null);
    setDwEmail("");
  };

  const handleEditUserInfo = (row) => {
    setEditUser(row);
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setEditUser(null);
  };

  const handleSeeTradingAccounts = (row) => {
    const id = row?.userId ?? row?.id;
    if (!id) return;

    markLeaveForReturn();
    const params = new URLSearchParams(location.search);
    params.set("tab", activeTab.toLowerCase());

    navigate(`/admin/trading-accounts-groups`, {
      state: {
        fromBrokersIbs: {
          pathname: location.pathname,
          search: `?${params.toString()}`,
        },
        brokersIbsTab: activeTab,
        brokersIbsSearch: search,
        brokersIbsSelection: {
          BROKERS: selectedBrokerIds,
          IBS: selectedIbIds,
        },
        brokerOrIbId: id,
        email: String(row?.email ?? "").trim(),
        name: String(row?.name ?? "").trim(),
        row,
      },
    });
  };

  const handleCopyLink = async (row) => {
    const ibId = row?.userId ?? row?.id;

    if (ibId === undefined || ibId === null || String(ibId).trim() === "") {
      setCopyLinkFeedback({
        open: true,
        severity: "error",
        message: "This IB does not have an ID available.",
      });
      return;
    }

    const referralLink = `${IB_REFERRAL_LINK_BASE}${encodeURIComponent(
      String(ibId).trim()
    )}`;

    try {
      await navigator.clipboard.writeText(referralLink);
      setCopyLinkFeedback({
        open: true,
        severity: "success",
        message: "IB referral link copied.",
      });
    } catch (copyError) {
      console.error("Unable to copy IB referral link:", copyError);
      setCopyLinkFeedback({
        open: true,
        severity: "error",
        message: "The IB referral link could not be copied.",
      });
    }
  };

  const handleExportFullReport = (row) => {
    const id = row?.userId ?? row?.id;
    markLeaveForReturn();
    try {
      localStorage.removeItem("reports_filters_state_v1");
      localStorage.removeItem("reports_client_response_v1");
      localStorage.removeItem("reports_full_rows_v1");
      localStorage.removeItem("reports_summary_rows_v1");
    } catch (storageError) {
      console.error("Could not clear reports localStorage before full export:", storageError);
    }

    if (id !== undefined && id !== null && String(id).trim() !== "") {
      try {
        localStorage.setItem(
          BROKERS_IBS_REPORT_SEARCH_ID_STORAGE_KEY,
          String(id).trim()
        );
      } catch (storageError) {
        console.error("Could not persist reports search id in localStorage:", storageError);
      }
    }

    navigate("/admin/reports/full-report");
  };

  const handleExportSummaryReport = (row) => {
    const id = row?.userId ?? row?.id;
    markLeaveForReturn();
    try {
      localStorage.removeItem("reports_filters_state_v1");
      localStorage.removeItem("reports_client_response_v1");
      localStorage.removeItem("reports_full_rows_v1");
      localStorage.removeItem("reports_summary_rows_v1");
    } catch (storageError) {
      console.error("Could not clear reports localStorage before summary export:", storageError);
    }

    if (id !== undefined && id !== null && String(id).trim() !== "") {
      try {
        localStorage.setItem(
          BROKERS_IBS_REPORT_SEARCH_ID_STORAGE_KEY,
          String(id).trim()
        );
      } catch (storageError) {
        console.error("Could not persist reports search id in localStorage:", storageError);
      }
    }

    navigate("/admin/reports/sumarize-reports");
  };

  const handleSeeWalletMovements = (row) => {
    const email = String(row?.email ?? "").trim();
    if (!email) {
      handleOpenError("This user has no email. Cannot open wallet.");
      return;
    }

    markLeaveForReturn();

    try {
      localStorage.setItem(BROKERS_WALLET_EMAIL_STORAGE_KEY, email);
    } catch (storageError) {
      console.error("Could not persist wallet email in localStorage:", storageError);
    }

    const params = new URLSearchParams(location.search);
    params.set("tab", activeTab.toLowerCase());

    navigate(`/admin/broker-wallets/${encodeURIComponent(email)}`, {
      state: {
        fromBrokersIbs: {
          pathname: location.pathname,
          search: `?${params.toString()}`,
        },
        brokersIbsTab: activeTab,
        brokersIbsSearch: search,
        brokersIbsSelection: {
          BROKERS: selectedBrokerIds,
          IBS: selectedIbIds,
        },
      },
    });
  };

  const handleTabChange = (_, newValue) => {
    const nextTab = normalizeBrokersIbsTab(newValue);
    if (nextTab === "ARCHIVED" && !isSuperAdmin) return;
    const params = new URLSearchParams(location.search);

    params.set("tab", nextTab.toLowerCase());

    setActiveTab(nextTab);
    if (nextTab !== "BROKERS") handleCloseSidePanel();

    navigate(
      {
        pathname: location.pathname,
        search: `?${params.toString()}`,
      },
      {
        replace: true,
        state: buildBrokersIbsLocationState(
          location.state,
          nextTab,
          {
            BROKERS: selectedBrokerIds,
            IBS: selectedIbIds,
          },
          search
        ),
      }
    );
  };

  return (
    <PageRoot>
      <Box ref={pageRef} sx={{ display: "contents" }}>
      <styles.TitleWrap sx={{ mb: 4 }}>
        <Typography
          sx={{
            fontFamily: "Plus Jakarta Sans",
            fontSize: "26px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "140%",
          }}
        >
          Brokers and IBs
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Take a look at all the brokers and IBs registered.
        </Typography>
      </styles.TitleWrap>

      <Paper
        elevation={2}
        sx={{
          ...styles.MainCard__body,
          padding: "20px",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          height: "auto",
          minHeight: "fit-content",
          overflow: "visible",
          flexGrow: 0,
          flexShrink: 0,
          alignSelf: "flex-start",
        }}
      >
        <Box sx={styles.cardSearch}>
          <TableToolTip title="The selected time period only affects the calculation of volume and profit." />

          <TextField
            variant="outlined"
            placeholder="Search..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            size="small"
            sx={{
              ...styles.InputStyles,
              width: 320,
              "& .MuiOutlinedInput-root": { borderRadius: "10px" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="outlined"
            sx={{
              ...styles.ExportButton,
              height: "40px",
              padding: "12px",
              border: "none",
              "&:hover": { border: "none" },
            }}
            onClick={() => {
              handleSearchChange("");
              setOpenSuccessClear(true);
            }}
          >
            <IconComponent icon="forward_media" size="12px" /> &nbsp;Clear
          </Button>

          {activeTab !== "ARCHIVED" && (
            <Button
              variant="outlined"
              sx={{
                ...styles.ExportButton,
                ...styles.ExportButtonRed,
                height: "40px",
                padding: "12px",
              }}
              onClick={() => setOpenCreateModal(true)}
            >
              + Create {activeTab === "BROKERS" ? "Broker" : "IB"}
            </Button>
          )}
        </Box>

        <Box
          sx={{
            mt: "-10px",
            mb: 1,
            borderRadius: "12px",
            border: "1px solid var(--Secondary-Global-Secondary-50, #ECF1F7)",
            background: "var(--Gray-White, #FFF)",
          }}
        >
          <Tabs
            value={showArchivedTab || activeTab !== "ARCHIVED" ? activeTab : "BROKERS"}
            onChange={handleTabChange}
            TabIndicatorProps={{
              children: <span className="MuiTabs-indicatorSpan" />,
            }}
            sx={{
              minHeight: 42,
              "& .MuiTabs-indicator": {
                display: "flex",
                justifyContent: "center",
                backgroundColor: "transparent",
                height: 4,
              },
              "& .MuiTabs-indicatorSpan": {
                width: "30%",
                margin: "0 auto",
                borderRadius: "2px",
                backgroundColor: "#15202A",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                minHeight: 42,
                padding: 0,
              },
              "& .MuiTab-root > span": {
                display: "block",
                width: "100%",
                textAlign: "center",
              },
            }}
          >
            <Tab value="BROKERS" label="Brokers" />
            <Tab value="IBS" label="IBs" />
            {showArchivedTab && <Tab value="ARCHIVED" label="Archived" />}
          </Tabs>
        </Box>

        {selectedUsersIds.length > 0 && (
          <Box
            sx={{
              px: 0,
              pt: 1.5,
              pb: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                background: "#EAF1FF",
                color: "#1F2A44",
                borderRadius: 999,
                px: 2,
                py: 0.75,
                fontSize: 12,
                fontWeight: 700,
                minWidth: 110,
                textAlign: "center",
              }}
            >
              Selected: {selectedUsersIds.length}
            </Box>

            <Button
              variant="outlined"
              onClick={handleEnable}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, px: 2 }}
            >
              Change to enabled
            </Button>

            <Button
              variant="outlined"
              onClick={handleDisable}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, px: 2 }}
            >
              Change to disabled
            </Button>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            gap: 2,
            width: "100%",
            alignItems: "stretch",
            px: 0,
            pb: 2.5,
            minWidth: 0,
            height: "auto",
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              flex: showSidePanel && activeTab === "BROKERS" ? "0 0 70%" : "1 1 100%",
              minWidth: 0,
              transition: "flex .2s ease",
              height: "auto",
              minHeight: "fit-content",
              display: "flex",
              flexDirection: "column",
            }}
            onMouseLeave={() => setActionHighlightRowId(null)}
          >
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <Table
                key={isArchivedView ? "archived-table" : "active-table"}
                rows={sortedRows}
                loading={
                  isArchivedView
                    ? archivedLoading || (!archivedLoaded && !archivedError)
                    : loading
                }
                error={isArchivedView && archivedError ? archivedError : null}
                checkboxSelection={!isArchivedView}
                archivedMode={isArchivedView}
                styles={styles}
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
                activeRowId={activePanelRowId}
                actionHighlightRowId={actionHighlightRowId}
                highlightActiveRow={showSidePanel && activeTab === "BROKERS"}
                rowSelectionModel={selectedUsersIds}
                onRowSelectionModelChange={handleRowSelectionChange}
                onDepositWithdraw={handleDepositWithdrawClick}
                onEditUserInfo={handleEditUserInfo}
                onSeeTradingAccounts={handleSeeTradingAccounts}
                onExportFullReport={handleExportFullReport}
                onExportSummaryReport={handleExportSummaryReport}
                onCopyLink={activeTab === "IBS" ? handleCopyLink : undefined}
                showBrokerCode={activeTab === "IBS"}
                onSeeWalletMovements={handleSeeWalletMovements}
                showDeleteAccount={isSuperAdmin}
                onDeleteAccount={isSuperAdmin ? handleOpenDeleteAccount : undefined}
                canDeleteAccount={(row) => isWalletBalanceZero(row)}
                isDeleteAccountDisabled={(row) => !isWalletBalanceZero(row)}
                showArchiveUser={isSuperAdmin}
                onArchiveUser={isSuperAdmin ? handleOpenArchiveAccount : undefined}
                onRestoreAccount={isSuperAdmin ? handleOpenRestoreAccount : undefined}
                onDeleteArchivedAccount={
                  isSuperAdmin ? handleOpenDeleteArchivedAccount : undefined
                }
                onActionMenuOpen={handleActionMenuOpen}
                onRowClick={handleOpenSidePanel}
                enableHorizontalScroll
                tableMinWidth={900}
                headerVariant="brokerTrees"
              />
            </Box>
          </Box>

          {showSidePanel && activeTab === "BROKERS" && (
            <Box
              sx={{
                flex: "0 0 30%",
                minWidth: 0,
                display: "flex",
                height: isPanelExpanded ? "auto" : BROKERS_IBS_PANEL_HEIGHT,
                maxHeight: isPanelExpanded ? "100vh" : BROKERS_IBS_PANEL_HEIGHT,
                minHeight: 0,
                mt: 1,
                pr: 2.5,
                position: "sticky",
                top: 16,
                alignSelf: "flex-start",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  borderRadius: 2,
                  overflow: "hidden",
                  background: "white",
                  border: "1px solid #E5E7EB",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  maxHeight: isPanelExpanded ? "100vh" : BROKERS_IBS_PANEL_HEIGHT,
                  minHeight: 0,
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexShrink: 0,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 20, color: "#111827" }}>
                      {activeTab === "BROKERS" ? "IBs for Broker" : "Selected IBs"}
                    </Typography>

                    <Button
                      variant="outlined"
                      onClick={() => setIsPanelExpanded((prev) => !prev)}
                      sx={{
                        ...styles.ExportButton,
                        border: "none",
                        minWidth: "auto",
                        px: 0.5,
                        "&:hover": {
                          border: "none",
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      <IconComponent icon="open_in_full" size="14px" /> &nbsp;Expand
                    </Button>
                  </Box>

                  <IconButton onClick={handleCloseSidePanel}>
                    <CloseIcon />
                  </IconButton>
                </Box>

                <Divider />

                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    px: 2.5,
                    py: 2,
                  }}
                >
                  {panelLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      {panelError && (
                        <Typography sx={{ color: "error.main", mb: 2 }}>
                          {panelError}
                        </Typography>
                      )}

                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
                        {(panelItems?.length > 0 ? panelItems : [EMPTY_IB_PANEL_ITEM]).map(
                          (item, idx) => (
                            <InfoCard key={`${item?.id ?? "x"}-${idx}`} item={item} />
                          )
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      <CreateBrokerIbModal
        open={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        onSave={async (formData) => {
          try {
            setLoading(true);
            const response = await brokersService.CreateBroker(formData);
            if (response.status !== 200) {
              handleOpenError(response.data.message ?? "Error creating user. Please try again.");
              return false;
            }

            const reloaded = await reloadUsers();
            if (!reloaded) {
              handleOpenError("Error refreshing brokers data. Please try again.");
              return false;
            }

            setCreateSuccessMessage(
              response?.data?.message ?? response?.message ?? ""
            );
            setOpenCreateModal(false);
            setOpenCreateSuccess(true);
            return true;
          } catch (e) {
            handleOpenError(e?.message || "Error creating user. Please try again.");
            return false;
          } finally {
            setLoading(false);
          }
        }}
      />

      <CreateUserSuccessModal
        open={openCreateSuccess}
        onClose={() => {
          setOpenCreateSuccess(false);
          setCreateSuccessMessage("");
        }}
        message={createSuccessMessage}
      />

      <EditBrokerIbModal
        open={openEditModal}
        onClose={handleCloseEditModal}
        user={editUser}
        onSave={async (payload) => {
          try {
            setLoading(true);
            const response = await brokersService.UpdateBroker(payload);
            if (response.status !== 200) {
              handleOpenError(response.data.message ?? "Error updating user. Please try again.");
              setLoading(false);
              handleCloseEditModal();
              return false;
            }

            const reloaded = await reloadUsers();
            if (!reloaded) {
              handleOpenError("Error refreshing brokers data. Please try again.");
              return false;
            }

            setUpdatedUserName(payload?.name ?? "");
            handleCloseEditModal();
            setOpenUpdateSuccess(true);
            return true;
          } catch (e) {
            handleOpenError(e?.message || "Error updating user. Please try again.");
            return false;
          } finally {
            setLoading(false);
          }
        }}
      />

      <UpdateUserSuccessModal
        open={openUpdateSuccess}
        onClose={() => setOpenUpdateSuccess(false)}
        name={updatedUserName}
      />

      <DepositWithdrawModal
        open={openDW}
        onClose={handleCloseDW}
        user={dwUser}
        loading={dwLoading}
        onSubmit={async (payloadFromModal) => {
          try {
            setDwLoading(true);

            if (!dwEmail) {
              console.error("Missing retained email for transaction.");
              handleOpenError("Missing retained email for transaction.");
              return false;
            }

            const uiType = String(payloadFromModal?.type ?? "").toLowerCase().trim();
            const apiType = uiType === "withdraw" ? "Withdraw" : "Deposit";
            const comment = String(payloadFromModal?.comment ?? "").trim();

            const finalPayload = {
              email: dwEmail,
              type: apiType,
              amount: Number(payloadFromModal?.amount),
              ...(comment ? { comment } : {}),
            };

            const response = await brokersService.BrokersTransactionCreate(finalPayload);
            if (!response) {
              handleOpenError("Error creating transaction. Please try again.");
              return false;
            }

            const reloaded = await reloadUsers();
            if (!reloaded) {
              handleOpenError("Error refreshing brokers data. Please try again.");
              return false;
            }

            return true;
          } catch (e) {
            handleOpenError(e?.message || "Error creating transaction. Please try again.");
            return false;
          } finally {
            setDwLoading(false);
          }
        }}
      />

      <ClearSuccessModal open={openSuccessClear} onClose={() => setOpenSuccessClear(false)} />

      <Snackbar
        open={copyLinkFeedback.open}
        autoHideDuration={3500}
        onClose={() =>
          setCopyLinkFeedback((current) => ({ ...current, open: false }))
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={copyLinkFeedback.severity}
          variant="filled"
          onClose={() =>
            setCopyLinkFeedback((current) => ({ ...current, open: false }))
          }
        >
          {copyLinkFeedback.message}
        </Alert>
      </Snackbar>

      <ErrorModal open={openErrorModal} onClose={handleCloseError} message={errorMessage} />

      <NotificationModal
        open={openDeleteConfirm}
        onClose={handleCloseDeleteConfirm}
        iconType="warning"
        title="Delete account"
        message={`This permanently deletes ${rowToDelete?.name || "this account"} and its information. This operation cannot be undone.`}
        onRefuseLabel="Cancel"
        onAcceptLabel={deletingAccount ? "Deleting..." : "Delete account"}
        onRefuse={handleCloseDeleteConfirm}
        onAccept={handleConfirmDeleteAccount}
      />

      <NotificationModal
        open={deleteNotice.open}
        onClose={() => setDeleteNotice((current) => ({ ...current, open: false }))}
        iconType={deleteNotice.iconType}
        title={deleteNotice.title}
        message={deleteNotice.message}
        onAcceptLabel="Close"
        onAccept={() => setDeleteNotice((current) => ({ ...current, open: false }))}
      />

      <NotificationModal
        open={openArchiveConfirm}
        onClose={handleCloseArchiveConfirm}
        iconType="warning"
        title="Archive user"
        message={ARCHIVE_CONFIRM_MESSAGE}
        onRefuseLabel="Cancel"
        onAcceptLabel="Archive user"
        onRefuse={handleCloseArchiveConfirm}
        onAccept={handleConfirmArchiveAccount}
      />

      <NotificationModal
        open={archivedActionConfirm.open}
        onClose={handleCloseArchivedActionConfirm}
        iconType="warning"
        title={
          archivedActionConfirm.type === "restore"
            ? "Restore account"
            : "Delete archived account"
        }
        message={
          archivedActionConfirm.type === "restore"
            ? RESTORE_CONFIRM_MESSAGE
            : DELETE_ARCHIVED_CONFIRM_MESSAGE
        }
        onRefuseLabel="Cancel"
        onAcceptLabel={archivedActionConfirm.type === "restore" ? "Restore" : "Delete"}
        onRefuse={handleCloseArchivedActionConfirm}
        onAccept={handleConfirmArchivedAction}
      />

      <NotificationModal
        open={accountActionNotice.open}
        onClose={() => setAccountActionNotice((current) => ({ ...current, open: false }))}
        iconType={accountActionNotice.iconType}
        title={accountActionNotice.title}
        message={accountActionNotice.message}
        onAcceptLabel="Close"
        onAccept={() => setAccountActionNotice((current) => ({ ...current, open: false }))}
      />
      </Box>
    </PageRoot>
  );
}

export default BrokerIbs;